// ─── Journal d'activité première partie (self-hosted, sans Google Analytics) ─
//
// File d'attente légère qui réplique les événements produits utilisateur vers
// NOTRE backend (`POST /activity`), en plus (et indépendamment) de GA. Envoi
// par LOTS pour limiter les requêtes : vidage périodique + au passage en
// arrière-plan / fermeture d'onglet (fetch keepalive). Best-effort : toute
// erreur est avalée, ne casse jamais l'app.
import { api } from '@/services/api';
import { getAuthToken } from '@/lib/authStorage';

export interface ActivityEventInput {
  name: string;
  examId?: string | null;
  examName?: string | null;
  categoryId?: string | null;
  categoryName?: string | null;
  searchTerm?: string | null;
  numValue?: number | null;
  deviceCategory?: string | null;
  path?: string | null;
  metadata?: Record<string, unknown> | null;
}

const FLUSH_INTERVAL_MS = 10_000;
const MAX_QUEUE = 100;
const MAX_BATCH = 40;

let queue: ActivityEventInput[] = [];
let started = false;

/** Ajoute un événement à la file et démarre le vidage périodique si besoin. */
export function logActivity(event: ActivityEventInput): void {
  if (!event?.name) return;
  // Chemin courant si non fourni (contexte de la page au moment de l'action).
  if (event.path === undefined && typeof window !== 'undefined') {
    event.path = window.location.pathname;
  }
  queue.push(event);
  if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
  ensureStarted();
}

function ensureStarted(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  setInterval(flush, FLUSH_INTERVAL_MS);
  // Vider avant que l'onglet ne parte (mobile inclus via visibilitychange).
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
  window.addEventListener('pagehide', () => flush(true));
}

/**
 * Envoie le lot courant. `useKeepalive` (déchargement de page) → fetch avec
 * `keepalive` pour survivre à la navigation. Sinon → axios normal.
 */
function flush(useKeepalive = false): void {
  if (queue.length === 0) return;
  const token = getAuthToken();
  if (!token) return; // pas de session → rien à envoyer (backend exige l'auth)

  const batch = queue.slice(0, MAX_BATCH);
  queue = queue.slice(batch.length);

  if (useKeepalive && typeof fetch === 'function') {
    try {
      const base = (api.defaults.baseURL || '').replace(/\/+$/, '');
      void fetch(`${base}/activity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ events: batch }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* noop */
    }
    return;
  }

  api.post('/activity', { events: batch }).catch(() => {
    // Échec réseau : on ne re-empile pas (best-effort, pas de perte critique).
  });
}
